import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../../components/Button';
import { AdminPageLayout, Card } from '../../components';
import PhotoUpload from '../../components/PhotoUpload';
import galleryApi from '../../services/galleryApi';
import { Image, ArrowLeft } from 'lucide-react';

const GalleryEdit = () => {
  const { imageId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    display_order: 0,
    active: true
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [replacePhoto, setReplacePhoto] = useState(false);

  useEffect(() => {
    loadImage();
  }, [imageId]);

  const loadImage = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all images and find the one we're editing
      const response = await galleryApi.getImages(false);
      const images = response.data?.images || [];
      const image = images.find(img => img.id === imageId);

      if (!image) {
        setError('Image not found');
        return;
      }

      setOriginalImage(image);
      setFormData({
        title: image.title,
        description: image.description || '',
        display_order: image.display_order,
        active: image.active
      });
      setPhotoPreview(image.image_url);
    } catch (err) {
      console.error('Error loading image:', err);
      setError('Failed to load image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = (fileInfo) => {
    setPhotoFile(fileInfo.file);
    setPhotoPreview(fileInfo.preview);
    setReplacePhoto(true);
  };

  const handlePhotoRemove = () => {
    setPhotoFile(null);
    setPhotoPreview(originalImage?.image_url || null);
    setReplacePhoto(false);
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setError(null);
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }

    if (formData.title.length > 100) {
      setError('Title must be 100 characters or less');
      return false;
    }

    if (formData.description.length > 500) {
      setError('Description must be 500 characters or less');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const updates = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        display_order: formData.display_order,
        active: formData.active
      };

      // If photo was replaced, we'd need to handle file upload
      // For now with dummy data, we just update the metadata
      if (replacePhoto && photoFile) {
        // TODO: Handle photo replacement when backend is ready
        // const formDataToSubmit = new FormData();
        // formDataToSubmit.append('file', photoFile);
        // await galleryApi.uploadReplacementImage(imageId, formDataToSubmit);
      }

      await galleryApi.updateImage(imageId, updates);

      // Navigate back to gallery management
      navigate('/admin/gallery');
    } catch (err) {
      console.error('Error updating image:', err);
      setError(err.response?.data?.detail || 'Failed to update image. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/gallery');
  };

  if (loading) {
    return (
      <AdminPageLayout
        title="EDIT GALLERY IMAGE"
        subtitle="Update image details"
      >
        <Card>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading image...</p>
          </div>
        </Card>
      </AdminPageLayout>
    );
  }

  if (error && !originalImage) {
    return (
      <AdminPageLayout
        title="EDIT GALLERY IMAGE"
        subtitle="Update image details"
      >
        <Card>
          <div className="error-container">
            <p>{error}</p>
            <Button onClick={() => navigate('/admin/gallery')}>
              Back to Gallery
            </Button>
          </div>
        </Card>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout
      title="EDIT GALLERY IMAGE"
      subtitle="Update image details and settings"
      primaryActions={
        <Button variant="secondary" onClick={handleCancel}>
          <ArrowLeft size={18} />
          Back to Gallery
        </Button>
      }
    >
      <Card>
        <form onSubmit={handleSubmit} className="gallery-upload-form">
          {error && (
            <div className="error-banner">
              <p>{error}</p>
            </div>
          )}

          {/* Photo Upload */}
          <div className="form-section">
            <h3 className="section-title">
              <Image size={20} />
              Image
            </h3>
            <PhotoUpload
              onPhotoSelect={handlePhotoSelect}
              photoUrl={photoPreview}
              onPhotoRemove={handlePhotoRemove}
              label={replacePhoto ? "Replace Gallery Image" : "Current Gallery Image"}
              hint="JPG, PNG, or WebP • Max 5MB • Click to replace the image"
              required={false}
            />
            {replacePhoto && (
              <div className="info-message">
                <p>You are replacing the current image. Click "Update Image" to save changes.</p>
              </div>
            )}
          </div>

          {/* Title */}
          <div className="form-section">
            <h3 className="section-title">Image Details</h3>

            <div className="form-group">
              <label htmlFor="title">
                Title <span className="required">*</span>
              </label>
              <input
                id="title"
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter image title"
                maxLength={100}
                required
              />
              <span className="field-hint">
                {formData.title.length}/100 characters
              </span>
            </div>

            {/* Description */}
            <div className="form-group">
              <label htmlFor="description">Description (Optional)</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Enter image description or caption"
                maxLength={500}
                rows={4}
              />
              <span className="field-hint">
                {formData.description.length}/500 characters • This will be shown as a caption in the carousel
              </span>
            </div>

            {/* Display Order */}
            <div className="form-group">
              <label htmlFor="display_order">Display Order</label>
              <input
                id="display_order"
                type="number"
                min="0"
                value={formData.display_order}
                onChange={(e) => handleInputChange('display_order', parseInt(e.target.value) || 0)}
              />
              <span className="field-hint">
                Lower numbers appear first in the carousel (0 = first)
              </span>
            </div>

            {/* Active Status */}
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                />
                <span>Image is active</span>
              </label>
              <span className="field-hint">
                Active images will appear in the public gallery carousel
              </span>
            </div>
          </div>

          {/* Image Metadata */}
          {originalImage && (
            <div className="form-section">
              <h3 className="section-title">Metadata</h3>
              <div className="metadata-grid">
                <div className="metadata-item">
                  <span className="metadata-label">Created:</span>
                  <span className="metadata-value">
                    {new Date(originalImage.created_at).toLocaleString()}
                  </span>
                </div>
                <div className="metadata-item">
                  <span className="metadata-label">Image ID:</span>
                  <span className="metadata-value">{originalImage.id}</span>
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Updating...' : 'Update Image'}
            </Button>
          </div>
        </form>
      </Card>
    </AdminPageLayout>
  );
};

export default GalleryEdit;
